/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : AiSuggestionContext.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  Typography,
  LinearProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import PropTypes from "prop-types";
import CustomDataGridV2 from "../common/datagrid/CustomDataGridV2";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useEffect, useState } from "react";
import helper from "../../../utils/helper";
import { useDispatch, useSelector } from "react-redux";
import { HandleApiActions } from "../../../redux/actions";
import smartMatchActions from "../../../redux/actions/smart-matching/smartMatchSuggestionActions";
import { FeedbackAction } from "./constants/feedbackTypes";
import CustomToolTip from "../../infoButton/CustomToolTip";
import TestCaseCampaignLinkDialog from "../../../pages/regulatory-compliance/create/TestCaseCampaignLinkDialog";

const suggestionStrengthColorMap = {
  high: "#26890D",
  medium: "#0076A8",
  low: "#111111",
};

const LOADING_MESSAGE_MAX_LENGTH = 50;

const truncateWithEllipsis = (text, maxLength = LOADING_MESSAGE_MAX_LENGTH) => {
  const message = String(text || "");
  return message.length > maxLength
    ? `${message.slice(0, maxLength).trimEnd()}...`
    : message;
};

const getUnlinkedTestCaseTooltip = (testCase) => {
  const missingLinks = [];

  if (testCase?.is_component_linked === false) {
    missingLinks.push("component");
  }
  if (testCase?.is_lr_linked === false) {
    missingLinks.push(
      missingLinks.length === 0 &&
        testCase?.is_component_linked !== false &&
        testCase?.is_campaign_linked !== false
        ? "legal requirements"
        : "legal requirement",
    );
  }
  if (testCase?.is_campaign_linked === false) {
    missingLinks.push("campaign");
  }

  return missingLinks.length
    ? `Test case not linked to any ${missingLinks.join(", ")}`
    : "";
};

/* =========================================================
   1. API → GRID ROW MAPPER
   ========================================================= */
const mapRecommendationsToRows = (recommendations, source) =>
  recommendations?.map((rec, index) => ({
    id: index + 1,

    sourceNodeId: source.nodeId,

    component: {
      number: rec?.targetContext?.number,
      description: rec?.targetContext?.description,
      version: rec?.targetContext?.version,
      zone: rec?.targetContext?.zone,
      interface:
        rec?.targetContext?.networkInterfaces
          ?.map((ni) => ni.name)
          .join(", ") || "N/A",
    },

    parentContext: {
      entityName: rec?.targetContext?.parent?.description || "N/A",
      entityType: rec?.targetContext?.parent?.entityType || "N/A",
      standardNumber:
        rec?.targetContext?.parentContext?.standardNumber || "N/A",
    },

    score: {
      // value: Math.floor(rec.finalScore * 100),
      value: rec.suggestionStrength,
      summary: rec.explanation.summary,
      recommendation: rec,
    },

    raw: rec, // keep full object if needed later

    isLinked: false, // UI state
    isLinking: false,

    feedback: null, // APPROVED | REJECTED | null
    isProvidingFeedback: false,

    contextualApplicability: rec.contextualApplicability !== false,
  }));

// Icon tool tips can be added later when functionality is defined
const AiSuggestionRowActionsTooltips = [
  "Approve Suggestion",
  "Reject Suggestion",
  "Link to this component",
  "Unlink from this component",
];

/* =========================================================
   2. GRID COLUMNS
   ========================================================= */

const getColumns = (onWhyThis, handleLink, handleUnlink, submitFeedback) => [
  {
    field: "component",
    headerName: "Component",
    minWidth: 320,
    sortable: false,
    renderCell: ({ value, row }) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "start",
          height: "100%",
          width: "100%",
          flexDirection: "column",
        }}
      >
        {row?.parentContext?.standardNumber &&
          row.parentContext.standardNumber !== "N/A" && (
            <Typography variant="body2" color="text.primary">
              <strong>
                {helper.toSentenceCase("Standard:")}{" "}
                {row.parentContext.standardNumber}
              </strong>
            </Typography>
          )}

        <Typography
          variant="body2"
          color="text.primary"
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          <strong>
            {helper.toSentenceCase("Number:")} {value.number}
          </strong>
          {row?.raw?.targetContext?.entityType === "TEST_CASE" &&
            getUnlinkedTestCaseTooltip(row.raw.targetContext) && (
              <CustomToolTip
                type="component"
                title={getUnlinkedTestCaseTooltip(row.raw.targetContext)}
              >
                <ErrorOutlineIcon sx={{ color: "black", fontSize: 18 }} />
              </CustomToolTip>
            )}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {helper.toSentenceCase("Description:")} {value.description}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {helper.toSentenceCase("Version:")} {value.version}
        </Typography>
        {value.zone && (
          <Typography variant="body2" color="text.secondary">
            {helper.toSentenceCase("Zone Details:")} {value.zone}
          </Typography>
        )}

      </Box>
    ),
  },

  {
    field: "parentContext",
    headerName: "EE Context",
    minWidth: 100,
    // flex: 3,
    sortable: false,
    renderCell: ({ value }) =>
      value?.entityName === "N/A" ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
            flexDirection: "column",
          }}
        >

        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            position: "relative",
            width: "100%",
            flexDirection: "column",
          }}
        >
          <Typography variant="body2">
            <strong>EE:{value.entityName}</strong>
          </Typography>
        </Box>
      ),
  },

  {
    field: "score",
    headerName: "Score",
    width: 120,
    minWidth: 110,
    sortable: false,
    align: "center",
    renderCell: ({ value, row }) => {
      const recommendation = row.raw;

      return (
        <Box sx={{ height: "100%" }}>

          <Box
            onClick={() => {
              onWhyThis(recommendation);
            }}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              width: "100%",
              flexDirection: "column",
              cursor: "pointer",
            }}
          >

            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                fontWeight: 600,
                lineHeight: 1,
                color:
                  suggestionStrengthColorMap[
                  String(value.value || "").toLowerCase()
                  ] || "#111111",
              }}
            >
              {value.value}
            </Typography>
          </Box>

        </Box>
      );
    },
  },

  {
    field: "raw",
    headerName: "",
    minWidth: 180,
    width: "fit-content",
    sortable: false,
    align: "right",
    renderCell: ({ row }) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          position: "relative",
          width: "100%",
        }}
      >
        <Tooltip title={AiSuggestionRowActionsTooltips[0]}>
          <IconButton
            size="small"
            disabled={
              row.isFeedbacking || row.feedback === FeedbackAction.APPROVED
            }
            onClick={() => submitFeedback(row, FeedbackAction.APPROVED)}
            sx={{
              color:
                row.feedback === FeedbackAction.APPROVED ? "#26890D" : "#000",
              ":hover": { color: "#26890D" },
            }}
          >
            <ThumbUpIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={AiSuggestionRowActionsTooltips[1]}>
          <IconButton
            size="small"
            disabled={
              row.isFeedbacking || row.feedback === FeedbackAction.REJECTED
            }
            onClick={() => submitFeedback(row, FeedbackAction.REJECTED)}
            sx={{
              color:
                row.feedback === FeedbackAction.REJECTED ? "#26890D" : "#000",
              ":hover": { color: "#26890D" },
            }}
          >
            <ThumbDownIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {row.isLinked ? (
          <Tooltip title={AiSuggestionRowActionsTooltips[3]}>
            <IconButton
              size="small"
              disabled={row.isLinking}
              onClick={() => handleUnlink(row.id)}
              sx={{ color: "#000", ":hover": { color: "#26890D" } }}
            >
              <RemoveCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title={AiSuggestionRowActionsTooltips[2]}>
            <IconButton
              size="small"
              disabled={row.isLinking}
              onClick={() => handleLink(row.id)}
              sx={{ color: "#000", ":hover": { color: "#26890D" } }}
            >
              <AddCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ),
  },
];

/* =========================================================
   3. COMPONENT
   ========================================================= */
export default function AISuggestionContent({
  status,
  data,
  loadingMessage = "AI suggestion loading...",
  loadingHistory = [],
  page = 1,
  rowsPerPage = 25,
  totalItems = 0,
  onPageChange = () => { },
  onWhyThis,
  onLinkRequest,
  onUnlinkRequest,
  onClose,
  closeDialogOnAdd = false,
}) {
  const dispatch = useDispatch();
  // const { selectedProjectDetails, selectedVariantDetails } = useSelector(
  //   (state) => state.projectOptions || {}
  // );
  const selectedProjectDetails = useSelector(
      (state) => state?.projectConfigReducer?.selectedProjectDetails,
    );
    const selectedVariantDetails = useSelector(
      (state) => state.projectConfigReducer.selectedVariantDetails,
    );
  
  const [rows, setRows] = useState([]);
  
  const [openCampaignLinkDialog, setOpenCampaignLinkDialog] = useState(false);
  const [testCasesToCampaignLink, setTestCasesToCampaignLink] = useState([]);
  const [pendingLinkData, setPendingLinkData] = useState(null);

  const clearCampaignLinkFields = () => {
    setOpenCampaignLinkDialog(false);
    setTestCasesToCampaignLink([]);
    setPendingLinkData(null);
  };

  const commitSelectedTestCases = () => {
    if (pendingLinkData) {
      onLinkRequest(pendingLinkData.selectionPayload);
      setRows((prev) =>
        prev.map((r) =>
          r.id === pendingLinkData.rowId ? { ...r, isLinked: true, isLinking: false } : r,
        ),
      );
      if (closeDialogOnAdd) {
        onClose();
      }
    }
    clearCampaignLinkFields();
  };

  const adaptRowToSelectionPayload = (row) => {
    const target = row.raw.targetContext;
    const entityType = row.raw.targetContext.entityType;

    // EE flow (existing, unchanged)
    if (entityType === "EE") {
      return {
        type: "EE",
        ee_id: target.entityId,
        assembly_number: target.number,
        description: target.description,
        version: target.version,
        linked_ee_ids: [],
      };
    }

    if (entityType === "SWM") {
      return {
        type: "SWM",
        swm_id: target.entityId,
        name: target.number,
        description: target.description,
        version: target.version,
        linked_swm_ids: [],
        ee_id: row?.raw?.targetContext?.parentContext?.parent_id,
      };
    }

    if (entityType === "VC") {
      return {
        type: "VC",
        vc_id: target.entityId,
        id: target.entityId,
        name: target.description,
        component_number: target.number,
        description: target.description,
        // version: target.version,
        pr_vs_id: row?.raw?.targetContext?.parentContext?.parent_vs_id,
        parentContext: row.raw.targetContext.parentContext,
        parent: row.raw.targetContext.parent,
        _ai: row.raw,
      };
    }

    if (entityType === "VS") {
      return {
        type: "VS",
        vs_id: target.entityId,
        id: target.entityId,
        name: target.description,
        system_number: target.number,
        description: target.description,
        version: target.version,
        _ai: row.raw,
      };
    }

    //  LEGAL flow (new)
    if (entityType === "LEGAL_REQUIREMENT") {
      return {
        type: "LEGAL_REQUIREMENT",
        lr_id: target.entityId,
        req_number: target.number,
        description: target.description,
        version: target.version,

        // this is what your Legal dialog expects
        parentContext: row.raw.targetContext.parentContext,

        // IMPORTANT: raw AI object preserved
        _ai: row.raw,
      };
    }

    if (entityType === "TEST_CASE") {
      return {
        type: "TEST_CASE",
        tc_id: target.entityId,
        test_case_id: target.entityId,
        manual_test_case_id: target?.number,
        req_number: target.number,
        description: target.description,
        version: target.version,
        is_component_linked: target?.is_component_linked !== false,
        is_lr_linked: target?.is_lr_linked !== false,
        is_campaign_linked: target?.is_campaign_linked !== false,

        // this is what your Legal dialog expects
        parentContext: row.raw.targetContext.parentContext,

        // IMPORTANT: raw AI object preserved
        _ai: row.raw,
      };
    }

    return null;
  };

  const adaptRowToEEItem = (row) => {
    const target = row.raw.targetContext;

    return {
      ee_id: target.entityId,
      assembly_number: target.number,
      description: target.description,
      version: target.version,

      // optional but safe defaults
      linked_ee_ids: [],
    };
  };

  useEffect(() => {
    if (data?.recommendations?.length) {
      setRows(mapRecommendationsToRows(data?.recommendations, data?.source));
    } else {
      setRows([]);
    }
  }, [data]);

  const handleLink = async (rowId) => {
    // identify row based on rowId
    const row = rows.find((r) => r.id === rowId);
    if (!row) return;
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, isLinking: true } : r)),
    );
    // create EE item from row data
    const selectionPayload = adaptRowToSelectionPayload(row);

    if (
      selectionPayload?.type === "TEST_CASE" &&
      selectionPayload?.is_campaign_linked === false
    ) {
      setRows((prev) =>
        prev.map((r) =>
          r.id === rowId ? { ...r, isLinking: false } : r,
        ),
      );
      setTestCasesToCampaignLink([
        {
          test_case_id: selectionPayload.tc_id,
          test_case_number: selectionPayload.req_number,
          description: selectionPayload.description,
          is_campaign_linked: selectionPayload.is_campaign_linked,
          _ai: selectionPayload._ai,
        },
      ]);
      setPendingLinkData({ rowId, selectionPayload });
      setOpenCampaignLinkDialog(true);
    } else {
      onLinkRequest(selectionPayload);
      setRows((prev) =>
        prev.map((r) =>
          r.id === rowId ? { ...r, isLinked: true, isLinking: false } : r,
        ),
      );
      if (closeDialogOnAdd) {
        onClose();
      }
    }
  };

  const handleUnlink = async (rowId) => {
    // identify row based on rowId
    const row = rows.find((r) => r.id === rowId);
    if (!row) return;
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, isLinking: true } : r)),
    );
    // create EE item from row data
    const eeItem = adaptRowToEEItem(row);
    //unlink API call
    onUnlinkRequest(eeItem);

    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId ? { ...r, isLinked: false, isLinking: false } : r,
      ),
    );
  };

  const submitFeedback = async (row, action) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === row.id ? { ...r, isProvidingFeedback: true } : r,
      ),
    );

    const payload = {
      sourceNodeId: row?.sourceNodeId,
      targetNodeId: row?.raw?.target?.entityId,
      action,
      context: {
        projectId: row?.raw?.projectId || "", // fill in later once backend supports additional context
        variantId: row?.raw?.variantId || "",
        eeNodeId: row?.raw?.targetContext?.entityId || "",
      },
    };

    const resp = await dispatch(
      HandleApiActions({
        https_method: "POST",
        ...smartMatchActions.submit_feedback,
        params: payload,
        show_toast: false,
        ignore_app_loading_spinner: true,
      }),
    );

    setRows((prev) =>
      prev.map((r) =>
        r.id === row.id
          ? {
            ...r,
            feedback: action,
            isProvidingFeedback: false,
          }
          : r,
      ),
    );

    if (resp?.error) {

      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id
            ? {
              ...r,
              feedback: null,
              isProvidingFeedback: false,
            }
            : r,
        ),
      );
    }
  };

  if (status === "loading") {
    const displayLoadingMessage = truncateWithEllipsis(loadingMessage);

    return (
      <Box
        sx={{
          p: 3,
          textAlign: "center",
          height: "360px",
          width: "650px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography>{displayLoadingMessage}</Typography>
        <Box sx={{ width: "100%", maxWidth: "320px" }}>
          <LinearProgress
            sx={{
              height: "5px",
              borderRadius: "0px",
              backgroundColor: "#e0e0e0",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#26890D",
                borderRadius: "0px",
              },
            }}
          />
        </Box>
      </Box>
    );
  }

  if (!data?.recommendations?.length) {
    return (
      <Box>
        <Box
          sx={{
            p: 3,
            textAlign: "center",
            color: "error.main",
            height: "360px",
            width: "650px",
          }}
        >
          <Typography>No suggestion found.</Typography>

        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "360px",
        width: "650px",
      }}
    >
      <CustomDataGridV2
        rows={rows}
        columns={getColumns(
          onWhyThis,
          handleLink,
          handleUnlink,
          submitFeedback,
        )}
        hideHeader
        showPagination={true}
        isMultiLine={true}
        page={page}
        rowsPerPage={rowsPerPage}
        totalItems={totalItems}
        handlePageChange={onPageChange}
      />
      <TestCaseCampaignLinkDialog
        open={openCampaignLinkDialog}
        testCasesToLink={testCasesToCampaignLink}
        projectId={selectedProjectDetails?.project_id}
        variantId={selectedVariantDetails?.variant_id}
        onClose={clearCampaignLinkFields}
        onSuccess={commitSelectedTestCases}
      />
    </Box>
  );
}

AISuggestionContent.propTypes = {
  status: PropTypes.string.isRequired,
  data: PropTypes.shape({
    recommendations: PropTypes.arrayOf(PropTypes.object),
    source: PropTypes.shape({
      nodeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  loadingMessage: PropTypes.string,
  loadingHistory: PropTypes.arrayOf(PropTypes.string),
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  totalItems: PropTypes.number,
  onPageChange: PropTypes.func,
  onWhyThis: PropTypes.func.isRequired,
  onLinkRequest: PropTypes.func.isRequired,
  onUnlinkRequest: PropTypes.func.isRequired,
  onClose: PropTypes.bool,
  closeDialogOnAdd: PropTypes.bool,
};
